import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProcessHeader from "components/ui/ProcessHeader";
import "../../styles/blueprint-print.css";

import BlueprintHero from "./components/BlueprintHero";
import HiddenValueAssets from "./components/HiddenValueAssets";
import AIAmplificationOpportunities from "./components/AIAmplificationOpportunities";
import RecommendedStartingPath from "./components/RecommendedStartingPath";
import LaunchPlan from "./components/LaunchPlan";
import StrategicEncouragement from "./components/StrategicEncouragement";
import FirstThreeSteps from "./components/FirstThreeSteps";
import ExportBlueprint from "./components/ExportBlueprint";
import ProspectusSnapshot from "./components/ProspectusSnapshot";
import BlueprintPrintLayout from "./components/BlueprintPrintLayout";

const generateBlueprint = (formData) => {
  const profession = formData?.profession || "management consulting";
  const skills = formData?.skills || "strategic planning, team leadership, process optimization";
  const interests = formData?.interests || "mentoring, writing, problem-solving";

  return {
    hiddenValueAssets: [
      {
        icon: "Brain",
        title: "Pattern Recognition",
        description: `After years in ${profession}, you can identify problems and solutions faster than someone with only theoretical knowledge. This is rare and highly compensable.`,
      },
      {
        icon: "Network",
        title: "Professional Network",
        description: "Your decades of relationships represent a warm audience of potential clients, referral partners, and collaborators that no newcomer can replicate overnight.",
      },
      {
        icon: "Shield",
        title: "Credibility & Trust",
        description: `Your track record in ${profession} signals reliability. Clients pay premium rates for proven expertise — not potential.`,
      },
      {
        icon: "Lightbulb",
        title: "Domain-Specific Judgment",
        description: `Your ability to navigate ${skills} comes from lived experience. This judgment is exactly what AI tools need human oversight to apply correctly.`,
      },
      {
        icon: "MessageSquare",
        title: "Communication Mastery",
        description: "You know how to translate complex ideas into clear, actionable guidance. This is a core skill for consulting, coaching, and content creation.",
      },
      {
        icon: "TrendingUp",
        title: "Results-Oriented Mindset",
        description: `Your career has been built on delivering outcomes. Clients in ${interests} contexts pay for results, not hours — and you know how to deliver them.`,
      },
    ],
    aiOpportunities: [
      {
        title: "AI-Assisted Expert Consulting",
        incomeModel: "Project-based fees: $2,500–$8,000 per engagement",
        whoItServes: "Small to mid-size businesses needing senior-level guidance without full-time executive costs",
        howAIHelps: "AI drafts reports, analyzes data, and creates deliverables in hours instead of days — multiplying your output without multiplying your hours",
        estimatedIncome: "$4,000–$12,000",
      },
      {
        title: "Digital Course or Workshop",
        incomeModel: "One-time purchase ($197–$997) or cohort enrollment ($500–$2,000)",
        whoItServes: "Professionals 10–20 years behind you who want to shortcut their learning curve",
        howAIHelps: "AI generates course outlines, writes scripts, creates slide decks, and produces marketing copy — reducing production time by 70%",
        estimatedIncome: "$2,000–$8,000",
      },
      {
        title: "Niche Advisory Retainer",
        incomeModel: "Monthly retainer: $1,500–$5,000 per client",
        whoItServes: "Founders and executives who need ongoing strategic guidance in your specific domain",
        howAIHelps: "AI handles research, competitive analysis, and document preparation — freeing you to focus on high-value strategic conversations",
        estimatedIncome: "$3,000–$15,000",
      },
    ],
    recommendedPath: {
      title: "AI-Assisted Expert Consulting",
      description: `Based on your background in ${profession} and your skills in ${skills}, the fastest path to income is positioning yourself as an AI-assisted consultant. You already have the expertise — AI simply makes you faster and more scalable.`,
      whyItFits: `Your ${profession} background gives you immediate credibility with businesses facing the exact challenges you have spent years solving.`,
      howItGeneratesIncome: "Charge project-based fees for defined deliverables. Start with a $2,500 discovery engagement to validate your offer before scaling.",
      feasibility: "You can have your first paying client within 30 days. The offer is simple, the market is proven, and your credentials do the heavy lifting.",
    },
    launchWeeks: [
      {
        theme: "Define Your Offer & Audience",
        tasks: [
          "Write a one-sentence description of the problem you solve and who you solve it for",
          "Identify 10 specific people in your network who match your ideal client profile",
          "Set your initial project fee — start at $2,500 for a defined deliverable",
          "Create a simple one-page PDF describing your service",
        ],
      },
      {
        theme: "Create Your Minimum Viable Service",
        tasks: [
          "Use AI to draft a client intake questionnaire (30 minutes with ChatGPT)",
          "Build a simple process document outlining your 3-step consulting approach",
          "Set up a free Calendly link for discovery calls",
          "Draft your outreach message — keep it to 3 sentences",
        ],
      },
      {
        theme: "Publish & Share",
        tasks: [
          "Update your LinkedIn headline to reflect your new consulting focus",
          "Post one piece of content sharing a specific insight from your career",
          "Send your outreach message to 5 people from your Week 1 list",
          "Ask two trusted colleagues for a brief written testimonial",
        ],
      },
      {
        theme: "Attract Your First Paying Clients",
        tasks: [
          "Follow up with everyone who responded to your Week 3 outreach",
          "Conduct at least 3 discovery calls — listen more than you talk",
          "Send a proposal to your most qualified prospect",
          "Celebrate your first paid engagement — then refine and repeat",
        ],
      },
    ],
    firstThreeSteps: [
      {
        title: "Write Your One-Sentence Offer",
        description: `Complete this sentence: 'I help [specific type of person] achieve [specific outcome] using my background in ${profession}.' Keep it simple. Clarity converts.`,
        timeEstimate: "20 minutes today",
      },
      {
        title: "Identify Your First 10 Prospects",
        description: "Open LinkedIn and write down 10 names of people who match your ideal client. These are warm contacts — people who already know and respect you. Do not overthink this list.",
        timeEstimate: "30 minutes this week",
      },
      {
        title: "Send One Outreach Message",
        description: "Choose the single best prospect from your list and send a brief, genuine message. Not a pitch — a conversation starter. Something like: 'I am exploring a new consulting focus and would love 15 minutes to get your perspective.'",
        timeEstimate: "10 minutes this week",
      },
    ],
  };
};

const mockFormData = {
  name: "Margaret",
  profession: "Healthcare Administration",
  skills: "operational efficiency, regulatory compliance, team leadership, budget management",
  interests: "mentoring emerging healthcare leaders, process improvement, patient experience",
  experience: "22 years in hospital administration, including 8 years as a department director",
  problems: "inefficient workflows, staff burnout, compliance gaps, poor patient communication",
};

const ProsperousIncomeBlueprint = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const formData = location?.state?.formData || mockFormData;
  const aiBlueprint = location?.state?.blueprint;
  const blueprint = useMemo(() => aiBlueprint || generateBlueprint(formData), [aiBlueprint, formData]);

  const handleRefine = () => {
    navigate("/professional-input-form");
  };

  return (
    <>
      <div className="min-h-screen screen-only" style={{ backgroundColor: "var(--color-background)" }}>
        <ProcessHeader completedSteps={["assessment"]} />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10 lg:py-12">
            <div className="flex flex-col gap-6 md:gap-8">

              {/* Export Button — Top */}
              <div className="flex justify-end no-print" data-no-print>
                <ExportBlueprint formData={formData} blueprint={blueprint} />
              </div>

              {/* Hero */}
              <BlueprintHero formData={formData} />

              {/* Prospectus Snapshot */}
              <ProspectusSnapshot blueprint={blueprint} />

              {/* Section 1 */}
              <HiddenValueAssets data={blueprint?.hiddenValueAssets} />

              {/* Section 2 */}
              <AIAmplificationOpportunities opportunities={blueprint?.aiOpportunities} />

              {/* Section 3 */}
              <RecommendedStartingPath path={blueprint?.recommendedPath} />

              {/* Section 4 */}
              <LaunchPlan weeks={blueprint?.launchWeeks} />

              {/* Section 5 */}
              <StrategicEncouragement />

              {/* Section 6 */}
              <FirstThreeSteps steps={blueprint?.firstThreeSteps} />

              {/* Export Button — Bottom */}
              <div
                className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl px-6 py-5 no-print"
                data-no-print
                style={{ background: "linear-gradient(135deg, #283ba3 0%, #1e2d8a 100%)", border: "2px solid #d3ac35" }}
              >
                <div>
                  <p className="font-bold text-white text-base" style={{ fontFamily: "var(--font-heading)" }}>
                    Please remain on this page until you download your report.
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: "#d3ac35", fontFamily: "var(--font-body)" }}>
                    Download as PDF or JSON to keep your personalized plan.
                  </p>
                </div>
                <ExportBlueprint formData={formData} blueprint={blueprint} />
              </div>

            </div>
          </div>
        </main>
        {/* Nautilus App Promo */}
<div className="border-t" style={{ borderColor: "var(--color-border)", backgroundColor: "#fff8ed" }}>
  <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 py-6 text-center">
    <p className="text-sm md:text-base" style={{ fontFamily: "var(--font-body)", color: "#283ba3" }}>
      If you'd like deeper clarity on your life purpose and identity, please be sure to explore the{" "}
      <a
        href="https://nautilus.quantume.earth"
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline underline-offset-2 transition-opacity hover:opacity-80"
        style={{ color: "#d3ac35" }}
      >
        QuantuMe Nautilus app
      </a>
      .
    </p>
  </div>
</div>
{/* Footer */}
<footer className="border-t mt-8" style={{ borderColor: "var(--color-border)", backgroundColor: "#fff8ed" }}>
        <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
           
            <img
              src="/assets/images/logo-prospectus-rocket-nobg-1774050607850.png"
              alt="QuantuMe Prospectus logo"
              className="h-6 w-auto"
            />
            <span className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-primary)" }}>
              QuantuMe<span style={{ color: "var(--color-accent)" }}></span>
            </span>
          </div>
          <p className="text-xs text-right" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
            &copy; {new Date()?.getFullYear()} <a href="https://quantume.earth/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-muted-foreground)" }}>QuantuMe LLC</a>. All insights are for personal reflection and growth purposes. Not intended as psychological, medical, or financial advice.
          </p>
        </div>
      </footer>
      </div>
      <BlueprintPrintLayout formData={formData} blueprint={blueprint} />
    </>
  );
};

export default ProsperousIncomeBlueprint;